"use client";

import { useEffect, useRef } from "react";
import { captureErrorBoundary } from "@/lib/utils/captureErrorBoundary";
import Link from "next/link";

export default function AuthError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const diagnosticsSentRef = useRef(false);

  useEffect(() => {
    if (diagnosticsSentRef.current) return;
    captureErrorBoundary(error, "auth", "/auth");
    diagnosticsSentRef.current = true;
  }, [error]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-(--bg-card) rounded-2xl p-8 shadow-lg border border-(--border-color) text-center">
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-(--status-danger-bg) flex items-center justify-center">
          <svg
            className="w-8 h-8 text-(--status-danger-text)"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        
        <h2 className="text-2xl font-['Font-Bold'] text-(--text-headline) mb-4">
          Something went wrong
        </h2>
        
        <p className="text-(--text-body) mb-6">
          We encountered an error while processing your request. Our team has been notified.
        </p>
        
        {error.digest && (
          <p className="text-sm text-(--text-muted) mb-6">
            Error ID: {error.digest}
          </p>
        )}
        
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={reset}
            className="px-6 py-3 bg-gradient-to-r from-(--brand) to-(--gold) text-(--dark-purple) font-bold rounded-lg hover:opacity-90 transition-opacity"
          >
            Try Again
          </button>
          
          <Link
            href="/"
            className="px-6 py-3 border border-(--border-color) text-(--text-body) font-medium rounded-lg hover:bg-(--bg-hover) transition-colors"
          >
            Go Home
          </Link>
        </div>
      </div>
    </div>
  );
}
